import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  CreditCard,
  CheckCircle,
  ArrowRight,
  Loader2,
  Star,
  Zap,
  Building2,
  Crown
} from 'lucide-react';
import { getSubscriptionPlans, getMySubscription, upgradeSubscription } from '../services/api';
import { toast } from 'sonner';

const Pricing = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [mySub, setMySub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        getSubscriptionPlans(),
        getMySubscription().catch(() => ({ data: null }))
      ]);
      setPlans(plansRes.data);
      setMySub(subRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    setUpgrading(planId);
    try {
      const res = await upgradeSubscription(planId);
      setMySub(res.data.subscription);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Upgrade');
    } finally {
      setUpgrading(null);
    }
  };

  const getPlanIcon = (planId) => {
    if (planId.includes('enterprise')) return Crown;
    if (planId.includes('premium')) return Zap;
    return Star;
  };

  const getPlanStyle = (planId) => {
    if (planId.includes('enterprise')) return 'border-yellow-400 bg-gradient-to-b from-yellow-50 to-white';
    if (planId.includes('premium_employer')) return 'border-primary bg-gradient-to-b from-orange-50 to-white ring-2 ring-primary/20';
    return '';
  };

  // Filter plans based on user type
  const relevantPlans = plans.filter(p => {
    if (user?.user_type === 'employer') return p.plan_id.includes('employer');
    return p.plan_id.includes('applicant') || p.plan_id === 'basic_employer';
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="pricing-page">
        <div className="text-center">
          <h1 className="text-3xl font-heading font-bold text-foreground flex items-center justify-center gap-3">
            <CreditCard className="w-8 h-8 text-primary" />
            Abonnements
          </h1>
          <p className="text-muted-foreground mt-2 max-w-lg mx-auto">
            Wählen Sie den passenden Plan für Ihre Anforderungen
          </p>
          {mySub && (
            <Badge className="mt-3 bg-primary/10 text-primary border-primary/20">
              Aktueller Plan: {mySub.plan_name}
            </Badge>
          )}
        </div>

        {/* Employer Plans */}
        {user?.user_type === 'employer' && (
          <div>
            <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Arbeitgeber-Pläne
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {plans.filter(p => p.plan_id.includes('employer')).map((plan) => {
                const Icon = getPlanIcon(plan.plan_id);
                const isCurrentPlan = mySub?.plan_id === plan.plan_id;
                const isPopular = plan.plan_id === 'premium_employer';

                return (
                  <Card key={plan.plan_id} className={`relative ${getPlanStyle(plan.plan_id)} transition-all hover:shadow-lg`} data-testid={`plan-${plan.plan_id}`}>
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="bg-primary text-white">Beliebt</Badge>
                      </div>
                    )}
                    <CardHeader className="text-center pb-2">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <CardTitle className="text-xl">{plan.name}</CardTitle>
                      <div className="mt-4">
                        <span className="text-4xl font-bold">{plan.price_monthly === 0 ? 'Kostenlos' : `${plan.price_monthly}€`}</span>
                        {plan.price_monthly > 0 && <span className="text-muted-foreground">/Monat</span>}
                      </div>
                      {plan.price_yearly > 0 && (
                        <p className="text-sm text-muted-foreground mt-1">
                          oder {plan.price_yearly}€/Jahr (spare {Math.round((1 - plan.price_yearly / (plan.price_monthly * 12)) * 100)}%)
                        </p>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <ul className="space-y-3">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <Button
                        className="w-full rounded-full"
                        variant={isCurrentPlan ? 'outline' : 'default'}
                        disabled={isCurrentPlan || upgrading === plan.plan_id}
                        onClick={() => handleUpgrade(plan.plan_id)}
                        data-testid={`upgrade-${plan.plan_id}`}
                      >
                        {upgrading === plan.plan_id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : isCurrentPlan ? (
                          'Aktueller Plan'
                        ) : (
                          <>
                            {plan.price_monthly === 0 ? 'Auswählen' : 'Upgrade'}
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Applicant Plans */}
        {user?.user_type === 'applicant' && (
          <div>
            <h2 className="text-xl font-heading font-semibold mb-4">Bewerber-Pläne</h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              {/* Free Plan */}
              <Card>
                <CardHeader className="text-center pb-2">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Star className="w-6 h-6 text-gray-600" />
                  </div>
                  <CardTitle>Kostenlos</CardTitle>
                  <span className="text-4xl font-bold">0€</span>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />Profil erstellen</li>
                    <li className="flex items-start gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />Jobs suchen & bewerben</li>
                    <li className="flex items-start gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />3 KI-Bewerbungen pro Monat</li>
                    <li className="flex items-start gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />Chat mit Übersetzung</li>
                  </ul>
                  <Button className="w-full rounded-full" variant="outline" disabled>
                    Aktueller Plan
                  </Button>
                </CardContent>
              </Card>

              {/* Premium Applicant */}
              {plans.filter(p => p.plan_id === 'premium_applicant').map(plan => (
                <Card key={plan.plan_id} className="border-primary ring-2 ring-primary/20" data-testid={`plan-${plan.plan_id}`}>
                  <CardHeader className="text-center pb-2">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Zap className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle>{plan.name}</CardTitle>
                    <div>
                      <span className="text-4xl font-bold">{plan.price_monthly}€</span>
                      <span className="text-muted-foreground">/Monat</span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ul className="space-y-3">
                      {plan.features.map((f, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />{f}
                        </li>
                      ))}
                    </ul>
                    <Button
                      className="w-full rounded-full"
                      disabled={mySub?.plan_id === plan.plan_id || upgrading === plan.plan_id}
                      onClick={() => handleUpgrade(plan.plan_id)}
                      data-testid={`upgrade-${plan.plan_id}`}
                    >
                      {upgrading === plan.plan_id ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : mySub?.plan_id === plan.plan_id ? 'Aktueller Plan' : (
                        <>Upgrade <ArrowRight className="w-4 h-4 ml-2" /></>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Pricing;
